"use client";

import Image from "next/image";
import {
  useActionState,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  AlertCircle,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  Mail,
  Phone,
  X,
} from "lucide-react";
import { loginAction, type LoginState } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const initialState: LoginState = {};
const rememberedLoginKey = "innova.nexus.rememberedLogin";
const passwordRecoveryContact = {
  email: "caua.vieira@innova.net.br",
  phone: "(21) 96753-1230",
  phoneHref: "+5521967531230",
};

function subscribeToRememberedLogin() {
  return () => undefined;
}

function getRememberedLoginSnapshot() {
  return window.localStorage.getItem(rememberedLoginKey) ?? "";
}

function getRememberedLoginServerSnapshot() {
  return "";
}

export function LoginForm() {
  const [state, action, pending] = useActionState(loginAction, initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const alertRef = useRef<HTMLDivElement>(null);
  const rememberedLogin = useSyncExternalStore(
    subscribeToRememberedLogin,
    getRememberedLoginSnapshot,
    getRememberedLoginServerSnapshot,
  );
  const [loginDraft, setLoginDraft] = useState<string | null>(null);
  const [rememberLoginDraft, setRememberLoginDraft] = useState<boolean | null>(
    null,
  );
  const login = loginDraft ?? rememberedLogin;
  const rememberLogin = rememberLoginDraft ?? Boolean(rememberedLogin);
  const formDisabled = pending || retryAfterSeconds > 0;

  useEffect(() => {
    const retryAfter = state.retryAfterSeconds ?? 0;
    const timeoutId = window.setTimeout(() => {
      setRetryAfterSeconds(retryAfter);
    }, 0);

    if (retryAfter <= 0) {
      return () => window.clearTimeout(timeoutId);
    }

    const intervalId = window.setInterval(() => {
      setRetryAfterSeconds((current) => Math.max(0, current - 1));
    }, 1_000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [state.retryAfterSeconds]);

  useEffect(() => {
    if (state.message && !pending) {
      alertRef.current?.focus();
    }
  }, [pending, state.message]);

  function handleLogin(formData: FormData) {
    const submittedLogin = String(formData.get("email") ?? "").trim();

    if (rememberLogin && submittedLogin) {
      window.localStorage.setItem(rememberedLoginKey, submittedLogin);
    } else {
      window.localStorage.removeItem(rememberedLoginKey);
    }

    action(formData);
  }

  return (
    <div className="rounded-[1.75rem] border border-[#3B82F6]/35 bg-[#07162D] p-5 text-slate-50 shadow-[0_24px_80px_rgba(0,21,41,0.24),0_0_42px_rgba(0,109,255,0.12)] sm:p-7">
      <div className="mb-7 text-center">
        <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl border border-[#00CFFF]/30 bg-[#020817]/70 shadow-[0_0_32px_rgba(0,207,255,0.22)]">
          <Image
            src="/brand/nexus-x-icon-dark.png"
            alt=""
            width={54}
            height={54}
            aria-hidden="true"
            className="h-12 w-12"
          />
        </div>
        <h2 className="text-2xl font-semibold tracking-normal text-[#F8FAFC]">
          Acesse sua conta
        </h2>
        <p className="mt-2 text-sm leading-5 text-[#94A3B8]">
          Entre para continuar no Nexus
        </p>
      </div>

      <form action={handleLogin} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-200">
            E-mail
          </Label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#00CFFF]/75"
              aria-hidden="true"
            />
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="seu@email.com"
              value={login}
              onChange={(event) => setLoginDraft(event.target.value)}
              required
              disabled={formDisabled}
              className="h-12 rounded-xl border-[#3B82F6]/30 bg-[#020817]/70 pl-10 text-slate-50 placeholder:text-slate-500 shadow-inner shadow-black/20 outline-none focus:border-[#00CFFF]/70 focus:ring-[#00CFFF]/25"
            />
          </div>
        </div>

        <div className="grid gap-2">
          <Label
            htmlFor="password"
            className="text-sm font-medium text-slate-200"
          >
            Senha
          </Label>
          <div className="relative">
            <LockKeyhole
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#00CFFF]/75"
              aria-hidden="true"
            />
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Digite sua senha"
              required
              disabled={formDisabled}
              className="h-12 rounded-xl border-[#3B82F6]/30 bg-[#020817]/70 px-10 text-slate-50 placeholder:text-slate-500 shadow-inner shadow-black/20 outline-none focus:border-[#00CFFF]/70 focus:ring-[#00CFFF]/25"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-slate-400 outline-none transition hover:bg-[#0F274C] hover:text-[#F8FAFC] focus-visible:ring-2 focus-visible:ring-[#00CFFF]/40 disabled:pointer-events-none disabled:opacity-50"
              onClick={() => setShowPassword((current) => !current)}
              disabled={formDisabled}
              aria-label={showPassword ? "Ocultar senha" : "Visualizar senha"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 pt-1 text-sm">
          <label className="inline-flex cursor-pointer items-center gap-3 rounded-md text-slate-300 focus-within:ring-2 focus-within:ring-[#00CFFF]/40">
            <input
              type="checkbox"
              className="sr-only"
              checked={rememberLogin}
              onChange={(event) => setRememberLoginDraft(event.target.checked)}
              disabled={formDisabled}
            />
            <span
              className="grid h-5 w-5 place-items-center rounded-md border border-[#3B82F6]/40 bg-[#020817]/80 text-[#020817] transition data-[checked=true]:border-[#00CFFF] data-[checked=true]:bg-[#00CFFF]"
              data-checked={rememberLogin}
              aria-hidden="true"
            >
              {rememberLogin ? <Check className="h-3.5 w-3.5" /> : null}
            </span>
            <span>Lembrar usuário</span>
          </label>

          <button
            type="button"
            onClick={() => setRecoveryOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={recoveryOpen}
            className="font-medium text-[#00CFFF] underline-offset-4 transition hover:text-sky-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00CFFF]/40"
          >
            Esqueci minha senha
          </button>
        </div>

        {state.message ? (
          <div
            ref={alertRef}
            className="flex gap-2 rounded-xl border border-red-400/35 bg-red-500/10 px-3 py-3 text-sm leading-5 text-red-100"
            role="alert"
            tabIndex={-1}
          >
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <p>{state.message}</p>
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={formDisabled}
          className="mt-2 h-12 w-full rounded-xl border border-[#00CFFF]/20 bg-[linear-gradient(135deg,#006DFF_0%,#00CFFF_100%)] text-base font-semibold text-white shadow-[0_14px_34px_rgba(0,109,255,0.34)] transition hover:shadow-[0_18px_44px_rgba(0,207,255,0.3)] focus-visible:ring-[#00CFFF]/40 disabled:opacity-70"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          )}
          {pending
            ? "Entrando..."
            : retryAfterSeconds > 0
              ? `Aguarde ${retryAfterSeconds}s`
              : "Entrar"}
        </Button>
      </form>

      <PasswordRecoveryDialog
        open={recoveryOpen}
        onClose={() => setRecoveryOpen(false)}
      />
    </div>
  );
}

function PasswordRecoveryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[#020817]/55 px-4 py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="w-full max-w-md rounded-2xl border border-[#3B82F6]/35 bg-[#07162D] p-6 text-slate-50 shadow-[0_24px_80px_rgba(0,21,41,0.45),0_0_42px_rgba(0,109,255,0.14)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-recovery-title"
      >
        <div className="flex items-start justify-between gap-5">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-[#00CFFF]">
              RECUPERAÇÃO DE ACESSO
            </p>
            <h2
              id="password-recovery-title"
              className="mt-2 text-xl font-semibold text-[#F8FAFC]"
            >
              Precisa de ajuda para entrar?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-[#0F274C] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00CFFF]/40"
            aria-label="Fechar recuperação de acesso"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="mt-4 text-sm leading-6 text-[#94A3B8]">
          Para redefinir sua senha, entre em contato com o administrador do
          Nexus pelos canais abaixo.
        </p>

        <div className="mt-6 grid gap-3">
          <a
            href={`mailto:${passwordRecoveryContact.email}?subject=Recuperar%20acesso%20ao%20Nexus`}
            className="flex items-center gap-3 rounded-xl border border-[#3B82F6]/25 bg-[#020817]/60 px-4 py-3 text-sm transition hover:border-[#00CFFF]/60 hover:bg-[#0F274C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00CFFF]/40"
          >
            <Mail className="h-4 w-4 shrink-0 text-[#00CFFF]" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-xs text-slate-400">E-mail</span>
              <span className="block truncate font-medium text-slate-100">
                {passwordRecoveryContact.email}
              </span>
            </span>
          </a>
          <a
            href={`tel:${passwordRecoveryContact.phoneHref}`}
            className="flex items-center gap-3 rounded-xl border border-[#3B82F6]/25 bg-[#020817]/60 px-4 py-3 text-sm transition hover:border-[#00CFFF]/60 hover:bg-[#0F274C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00CFFF]/40"
          >
            <Phone className="h-4 w-4 shrink-0 text-[#00CFFF]" aria-hidden="true" />
            <span>
              <span className="block text-xs text-slate-400">Telefone</span>
              <span className="block font-medium text-slate-100">
                {passwordRecoveryContact.phone}
              </span>
            </span>
          </a>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="mt-6 w-full border-[#3B82F6]/35 bg-transparent text-slate-100 hover:bg-[#0F274C] hover:text-white"
        >
          Fechar
        </Button>
      </section>
    </div>
  );
}
